import { useGetAgendaByIdQuery } from 'store/services/flow/agenda';
import { useRemoveFavoriteAgendaMutation, useSetFavoriteAgendaMutation } from 'store/services/flow/agendasFavorite';
import { memo } from 'react';
import { useLogin } from 'contexts';
import { useSpamGuard, useSetFavoriteTemplate } from 'utils/hooks';

import { UserPermissions } from 'types/enums';
import { CircularProgress } from '@mui/material';
import { Add, Star, StarBorder } from '@mui/icons-material';
import { IconButton } from 'components/mui/common/IconButton';
import { CustomBadge } from 'components/mui/common/CustomBadge';
import { useDispatch } from 'react-redux';
import { coreApi } from 'store/services/core';
import { TAG_TYPES } from 'types/services';
import { isFunction } from 'radash';
import { toTestIdSegment } from 'utils/testId';
import { ButtonWrapper, Count, Description, Header, Title, Wrapper } from './Card.styles';

export interface CardProps {
  title: string;
  agendaId: string;
  sourceType: string;
  description?: string;
  count?: number;
  isPinned?: boolean;
  onClick?: (agendaSourceType: string, agendaId: string) => void;
  onAddClick?: (agendaSourceType: string, agendaId: string) => void;
  mock?: boolean;
  overview?: boolean;
  unreadNotificationsCount?: number;
  isPlainIcon?: boolean;
}

export const Card = memo(
  ({
    title,
    count,
    description,
    agendaId,
    sourceType,
    isPinned,
    overview,
    unreadNotificationsCount,
    onClick,
    onAddClick,
    mock = false,
    isPlainIcon = false,
  }: CardProps) => {
    const dispatch = useDispatch();
    const { permissions } = useLogin();
    const [setFavoriteAgenda, { isLoading: isSettingFavoriteAgenda }] = useSetFavoriteAgendaMutation();
    const [removeFavoriteAgenda, { isLoading: isRemovingFavoriteAgenda }] = useRemoveFavoriteAgendaMutation();
    const { data: { userPermissions = [] } = {} } = useGetAgendaByIdQuery({ id: agendaId }, { skip: !agendaId || sourceType === 'Helios' });

    const {
      isPinned: heliosIsPinned,
      toggleFavoriteTemplate: toggleHeliosFavorite,
      isPending: isHeliosFavoritePending,
    } = useSetFavoriteTemplate({ templateId: agendaId, skip: sourceType !== 'Helios' });

    const { isPending: isAgendaFavoritePending, execute: guardedAgendaToggle } = useSpamGuard(isSettingFavoriteAgenda, isRemovingFavoriteAgenda);

    const isFavoriteMutationPending = isHeliosFavoritePending || isAgendaFavoritePending;

    const hasCreatePermission = permissions?.templateAdmin || userPermissions.includes(UserPermissions.CREATION);

    const onClickHandler = () => {
      if (isFunction(onClick) && !mock) {
        onClick(sourceType, agendaId);
      }
    };

    const onAddClickHandler = () => {
      if (isFunction(onAddClick) && !mock) {
        onAddClick(sourceType, agendaId);
      }
    };

    const toggleFavoriteAgenda = async () => {
      if (mock) {
        return;
      }

      if (sourceType === 'Helios') {
        await toggleHeliosFavorite();
      } else {
        await guardedAgendaToggle(async () => {
          await (isPinned ? removeFavoriteAgenda : setFavoriteAgenda)(agendaId);
          dispatch(coreApi.util.invalidateTags([TAG_TYPES.MODULES]));
        });
      }
    };

    const testId = `user-dashboard-agenda-card-${toTestIdSegment(agendaId)}`;

    return (
      <Wrapper data-testid={testId}>
        <Header onClick={onClickHandler} mock={mock.toString()}>
          <div>
            <div>
              <Title className="card-title">{title}</Title>
              {!!unreadNotificationsCount && (
                <CustomBadge data-testid={`${testId}-badge`} sx={{ padding: '3px 6px', fontSize: '12px' }}>
                  {unreadNotificationsCount}
                </CustomBadge>
              )}
            </div>
            <div>
              {count && (
                <Count variant="subtitle2" color="success">
                  +{count}
                </Count>
              )}
              <Description variant="subtitle2">{description}</Description>
            </div>
          </div>
        </Header>
        {overview && hasCreatePermission && sourceType !== 'Helios' && (
          <ButtonWrapper onClick={onAddClickHandler} disabled={mock} isPlainIcon={isPlainIcon} data-testid={`${testId}-add-button`}>
            <IconButton size={20} disabled={mock}>
              <Add fontSize="small" />
            </IconButton>
          </ButtonWrapper>
        )}
        {!overview && (
          <ButtonWrapper
            onClick={toggleFavoriteAgenda}
            disabled={mock || isFavoriteMutationPending}
            isPlainIcon={isPlainIcon}
            data-testid={`${testId}-favorite-button`}>
            <IconButton size={20} disabled={mock || isFavoriteMutationPending}>
              {isFavoriteMutationPending ? (
                <CircularProgress size={14} thickness={5} />
              ) : (sourceType === 'Helios' ? heliosIsPinned : isPinned) ? (
                <Star fontSize="small" />
              ) : (
                <StarBorder fontSize="small" />
              )}
            </IconButton>
          </ButtonWrapper>
        )}
      </Wrapper>
    );
  }
);
